import type {
  ExtractRequiredFields,
  OrganizationWithMembersCount,
  SuperAdminOrganizationListDataPaginated,
  SuperAdminOrganizationListDataPartialPaginated,
} from '@cmoh/shared'
import { useMemo } from 'react'
import { useTableWithPersistence } from '@/components/DataTable'
import { useApiQuery } from '@/hooks/use-api'
import { createStatusFieldMappings } from '@/hooks/use-field-mappings'
import { useTranslation } from '@/hooks/use-translation'
import { getMembersCountInfo, getOrganizationStatus } from '../utils/organization.utils'

type OrganizationsForUsers = {
  organizations: Array<
    ExtractRequiredFields<OrganizationWithMembersCount, 'id' | 'name'> & {
      primaryWebsiteUrl?: string | null
    }
  >
  pagination: SuperAdminOrganizationListDataPartialPaginated['pagination']
}

/**
 * Hook that manages paginated organizations data with local state and debounced API calls.
 */
export function useOrganizations() {
  const t = useTranslation()

  // Create field mappings for status field (inferred from deletedAt)
  const fieldMappings = useMemo(
    () => ({
      ...createStatusFieldMappings('status', 'deletedAt', '1'),
    }),
    [],
  )

  const tableData = useTableWithPersistence<SuperAdminOrganizationListDataPaginated>({
    tableId: 'organizations-table',
    endpoint: '/super-admin/organizations',
    queryKey: 'organizations',
    fieldMappings,
  })

  const getOrganizationStatusInfo = useMemo(
    () => (organization: OrganizationWithMembersCount) => {
      return getOrganizationStatus(organization, {
        active: t('superAdmin.organizations.active'),
        inactive: t('superAdmin.organizations.inactive'),
      })
    },
    [t],
  )

  const getMembersCountInfoForOrg = useMemo(
    () => (organization: OrganizationWithMembersCount) => {
      return getMembersCountInfo(organization.membersCount, {
        member: t('superAdmin.organizations.member'),
        members: t('superAdmin.organizations.members'),
      })
    },
    [t],
  )

  const statusFilterOptions = useMemo(
    () => ({
      ACTIVE: '1',
      INACTIVE: '0',
    }),
    [],
  )

  return useMemo(
    () => ({
      ...tableData,
      data: tableData.data?.organizations || [],
      pagination: tableData.data?.pagination || {
        total: 0,
        page: 1,
        per_page: 20,
        total_pages: 0,
      },
      getOrganizationStatusInfo,
      getMembersCountInfoForOrg,
      statusFilterOptions,
    }),
    [tableData, getOrganizationStatusInfo, getMembersCountInfoForOrg, statusFilterOptions],
  )
}

/**
 * Hook for fetching organizations list for user assignment.
 * Returns organizations that can be assigned to users.
 */
export function useOrganizationsForUsers() {
  return useApiQuery<OrganizationsForUsers>(
    ['organizations-for-users'],
    '/super-admin/organizations',
    {
      params: {
        all: true,
        fields: 'id,name',
        filters: JSON.stringify({
          groups: [{ conditions: [{ field: 'deletedAt', operator: 'is_null' }] }],
        }),
      },
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  )
}
